import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { Wand2, Loader } from "lucide-react";
import { useTranslation } from "react-i18next";

interface AIHelperButtonProps {
  isMobile: boolean;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onGenerate: (input: string) => void;
  isGenerating: boolean;
}

const AIHelperButton = ({
  isMobile,
  isOpen,
  onOpenChange,
  onGenerate,
  isGenerating,
}: AIHelperButtonProps) => {
  const { t } = useTranslation();
  const [userInput, setUserInput] = useState("");

  const handleGenerate = () => {
    onGenerate(userInput);
    setUserInput("");
  };

  const content = (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t('templates.ai_helper.description')}
      </p>
      <Textarea
        value={userInput}
        onChange={(e) => setUserInput(e.target.value)}
        placeholder={t('templates.ai_helper.placeholder')}
        className={isMobile ? "min-h-[120px]" : "min-h-[100px]"}
      />
      <Button
        onClick={handleGenerate}
        disabled={isGenerating || !userInput.trim()}
        className="w-full flex items-center justify-center gap-2"
      >
        {isGenerating ? (
          <>
            <Loader size={16} className="animate-spin" />
            <span>{t('templates.ai_helper.generating')}</span>
          </>
        ) : (
          <>
            <Wand2 size={16} />
            <span>{t('templates.ai_helper.generate_btn')}</span>
          </>
        )}
      </Button>
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={onOpenChange}>
        <Button type="button" variant="outline" size="sm" className="flex items-center gap-1 text-xs" onClick={() => onOpenChange(true)}>
          <Wand2 size={14} />
          {t('templates.ai_helper.button')}
        </Button>
        <SheetContent side="bottom" className="h-[100vh] p-0">
          <div className="flex flex-col h-full">
            <SheetHeader className="p-6 border-b">
              <SheetTitle className="text-left">{t('templates.ai_helper.title_mobile')}</SheetTitle>
            </SheetHeader>
            <div className="flex-1 p-6">
              {content}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <Button type="button" variant="outline" size="sm" className="flex items-center gap-1 text-xs" onClick={() => onOpenChange(true)}>
        <Wand2 size={14} />
        {t('templates.ai_helper.button')}
      </Button>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('templates.ai_helper.title_desktop')}</DialogTitle>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
};

export default AIHelperButton;